import { RowNode } from '@ag-grid-community/core';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  Renderer2,
  NgZone,
  inject,
  input,
  output,
  viewChild,
  viewChildren,
} from '@angular/core';
import { BehaviorSubject, Subject, combineLatest, debounceTime, filter, map, take, takeUntil, tap, withLatestFrom, fromEvent } from 'rxjs';
import { Models } from '../../../pipeline/models/global.models';
import { RouteApiService } from '../../../pipeline/stores/api/route-api.service';
import { RouteUiService } from '../../../pipeline/stores/ui/route-ui.service';
import { Popover } from 'primeng/popover';
import { possibleOpportunities } from '../../../opportunity-analysis/models/opportunity.models';
import { TagsRepository } from '../../../pipeline/stores/api/tags.repository';
import { startWith } from 'rxjs/operators';
import { TagsChipsService } from '../../services/tags-chips.service';
import { NgClass, NgStyle, AsyncPipe } from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { ScrollPanel } from 'primeng/scrollpanel';
import { TagStylePipe } from './pipes/tag-style.pipe';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'cmg-clear2-ui-tags-chips',
  templateUrl: './tags-chips.component.html',
  styleUrls: ['./tags-chips.component.scss'],
  imports: [NgClass, ButtonDirective, NgStyle, Popover, ScrollPanel, AsyncPipe, TagStylePipe],
})
export class TagsChipsComponent implements AfterViewChecked, OnInit, OnDestroy, OnChanges {
  private readonly _api = inject(RouteApiService);
  private readonly _ui = inject(RouteUiService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _tagsRepository = inject(TagsRepository);
  private readonly _tagsChipsService = inject(TagsChipsService);
  private readonly _renderer = inject(Renderer2);
  private readonly _ngZone = inject(NgZone);

  private readonly _readonlyTags = ['Figure'];

  private readonly _tagsCell = viewChild<ElementRef<HTMLElement>>('tagsCell');
  private readonly _overlayPanel = viewChild<Popover>('op');
  private readonly _addTagBtn = viewChild<ElementRef<HTMLElement>>('addTagBtn');
  private readonly _tagsCount = viewChild<ElementRef<HTMLElement>>('tagsCount');
  private readonly _boltMenuHandler = viewChild<ElementRef<HTMLElement>>('boltMenuHandler');
  private readonly _tagItem = viewChildren<ElementRef<HTMLElement>>('tagItem');
  readonly isInDashboard = input<boolean>(false);
  readonly tagGuids = input<string[] | null>([]);
  readonly entityGuid = input('');
  readonly fileDataId = input(0);
  readonly postOnAddingTag = input(true);
  readonly deleteOnRemovingTag = input(true);
  readonly useHiddenTag = input(true);
  readonly hasClassRow = input(false);
  @Input() tagLocation: 'grid' | 'sidebar' | 'dashboard' = 'grid';
  readonly rowNode = input<RowNode | undefined>(undefined);
  readonly isEditable = input(true);
  readonly hideTagCount = input(false);
  /** Optional custom sort order by tag name. When provided, tags are sorted by index in this array instead of alphabetically. */
  readonly tagSortOrder = input<string[] | null>(null);
  @Input() set pipelineType(value: Models.PipelineType) {
    this.pipelineType$.next(value);
  }

  readonly tagAdded = output<string[]>();
  readonly tagRemoved = output<string[]>();
  readonly tagClicked = output<Models.Tag>();
  private readonly _selectedTagsPanel = viewChild<Popover>('selectedTagsPanel');
  isDropdownOpen: boolean = false;
  pipelineType$ = new BehaviorSubject<Models.PipelineType | null>(null);
  tags: Set<Models.Tag> = new Set();
  selectedTags$ = new BehaviorSubject<Set<Models.Tag>>(new Set());
  canUpdateTagsCount$ = new BehaviorSubject<boolean>(false);
  hiddenTagsCount = 0;
  hiddenTagIndexes: number[] = [];
  tagsStyles$ = this._tagsRepository.all$;

  // on the dashboard we need to account for padding on the td element since the tags cell takes up the full width of the card
  private readonly _tagsCellContainerPadding = 22;
  tagsCellId: string;

  toggleDropdown(event: MouseEvent) {
    this.isDropdownOpen = !this.isDropdownOpen;
    this._tagsChipsService.setDropdown(this.isDropdownOpen ? this.tagsCellId : null);
    event.stopPropagation();
  }

  /** All global tags for the active Pipeline Type. */
  globalTags$ = combineLatest([this._api.tags.all$, this._ui.activePipelineType$, this.pipelineType$]).pipe(
    filter(([allTags]) => !!allTags && allTags?.length > 0),
    map(([allTags, activePipelineType, inputPipelineType]) => {
      return this._assembleTags(allTags, inputPipelineType ?? activePipelineType);
    }),
  );

  filteredUnselectedTags$ = combineLatest({
    globalTags: this.globalTags$,
    selectedTags: this.selectedTags$,
  }).pipe(
    filter(({ globalTags }) => globalTags?.length > 0),
    map(({ globalTags, selectedTags }) => {
      const arr = Array.from(selectedTags.values());
      return globalTags.filter((tag) => !arr.some((item: Models.Tag) => item.guid === tag.guid)).filter((tag) => !this.isTagReadOnly(tag));
    }),
  );

  private _destroyed$ = new Subject();

  constructor() {
    this.tagsCellId = this._tagsChipsService.getUniqueId();

    if (this.tagLocation === 'sidebar' || this.tagLocation === 'dashboard') {
      this._initializeTagCount();
    }
  }

  ngOnInit() {
    this._ui.closeDropdowns$.pipe(takeUntil(this._destroyed$)).subscribe((keepOpen) => {
      const _overlayPanel = this._overlayPanel();
      if (keepOpen !== _overlayPanel) {
        _overlayPanel.hide();
      }
    });
    this._updatePriorityTagOnViewChange();

    this.canUpdateTagsCount$
      .pipe(
        takeUntil(this._destroyed$),
        filter((canUpdate) => this.useHiddenTag() && canUpdate),
        debounceTime(0),
      )
      .subscribe(() => {
        this.hiddenTagsCount = this._tagsCell()?.nativeElement?.querySelectorAll('.chip.hidden-tag').length || 0;
        this.canUpdateTagsCount$.next(false);
        this._cdr.detectChanges();
      });

    this._tagsChipsService
      .getDropdownStatus()
      .pipe(
        takeUntil(this._destroyed$),
        filter((idx) => idx !== null && idx !== this.tagsCellId),
      )
      .subscribe(() => (this.isDropdownOpen = false));

    this.selectedTags$
      .pipe(
        takeUntil(this._destroyed$),
        map((tags) => {
          return Array.from(tags.values())?.length > 0 ? Array.from(tags.values()) : null;
        }),
      )
      .subscribe((tags) => {
        if (!tags || tags.length === 0) {
          this.isDropdownOpen = false;
        }
      });
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  ngOnChanges() {
    if (this.tagGuids()?.length > 0) {
      this.globalTags$
        .pipe(
          take(1),
          tap((availableTags) => {
            // TODO: Determine if we should be clearing for all scenarios.
            if (this._ui.store.value.activePipelineType === Models.PipelineType.ServicingLoan) {
              this.tags.clear();
            }

            for (const tagGuid of this.tagGuids()) {
              const newTag = availableTags.find((tag) => tag.guid === tagGuid);
              if (newTag) {
                this.tags.add(newTag);
              }
            }
            this.tags = this._sortTagsSet(this.tags);
            this.selectedTags$.next(this.tags);
            this._cdr.detectChanges();
          }),
        )
        .subscribe();
    }
  }

  private _initializeTagCount(): void {
    combineLatest({
      tagsWrapper: toObservable(this._tagItem).pipe(
        // Debounce ensures we don't calculate on every single intermediate resize step
        debounceTime(250),
      ),
      resize: fromEvent(window, 'resize').pipe(startWith(null), debounceTime(250)),
    })
      .pipe(takeUntil(this._destroyed$))
      .subscribe(() => {
        // 1. Run heavy DOM calculations outside Angular to prevent unnecessary Change Detection cycles
        this._ngZone.runOutsideAngular(() => {
          this._calculateVisibleTags();
        });
      });
  }

  private _calculateVisibleTags(): void {
    const allTags = this._tagItem();
    if (!allTags.length) {
      return;
    }

    // --- PHASE 1: BATCH DOM WRITES (Reset State) ---
    // Remove wrapping and unhide all tags first so we can measure their true natural width.
    const _tagsCell = this._tagsCell();
    this._renderer.removeClass(_tagsCell.nativeElement, 'wrap');

    allTags.forEach((tag) => {
      this._renderer.removeClass(tag.nativeElement, 'hidden-tag');
    });

    // --- PHASE 2: BATCH DOM READS (Measurements) ---
    // Read all dimensions at once. This prevents layout thrashing.

    // Measure fixed elements
    let accumulativeWidth =
      this._getElementWidth(this._addTagBtn()?.nativeElement, 8) +
      this._getElementWidth(this._tagsCount()?.nativeElement, 8) +
      this._getElementWidth(this._boltMenuHandler()?.nativeElement);

    // Measure Container
    const containerWidth =
      this.tagLocation === 'dashboard'
        ? _tagsCell.nativeElement.parentElement.parentElement.getBoundingClientRect().width - this._tagsCellContainerPadding
        : _tagsCell.nativeElement.getBoundingClientRect().width;

    // Measure all tag widths into an array
    const tagWidths = allTags.map((tag) => tag.nativeElement.getBoundingClientRect().width + 8);
    // --- PHASE 3: CALCULATION (Pure Logic) ---
    const tagsToHide: number[] = []; // Store indices of tags to hide
    if (this.useHiddenTag()) {
      tagWidths.forEach((width, index) => {
        if (accumulativeWidth + width < containerWidth) {
          accumulativeWidth += width;
        } else {
          tagsToHide.push(index);
        }
      });
    }

    // --- PHASE 4: BATCH DOM WRITES (Apply Changes) ---

    // Apply hidden class to calculated tags
    tagsToHide.forEach((index) => {
      this._renderer.addClass(allTags[index].nativeElement, 'hidden-tag');
    });

    // Restore wrap class
    this._renderer.addClass(_tagsCell.nativeElement, 'wrap');

    // Handle "Force First Tag Visible" logic
    if (this.tagLocation !== 'dashboard' && tagsToHide.length === allTags.length) {
      // If all tags are hidden, force show the first one
      this._renderer.removeClass(allTags[0].nativeElement, 'hidden-tag');
    }

    // --- PHASE 5: RE-ENTER ZONE ---
    // Only now do we tell Angular to update the view and notify others
    this._ngZone.run(() => {
      this._cdr.detectChanges();
      this.canUpdateTagsCount$.next(true);
    });
  }

  private _getElementWidth(element: HTMLElement | null, offset: number = 0): number {
    return element ? element.getBoundingClientRect().width + offset : 0;
  }

  ngAfterViewChecked(): void {
    if (this.tagLocation === 'grid' && !this.isInDashboard()) {
      // Add cell hover effect
      const childNodes = Array.from(this._tagsCell().nativeElement.children);
      const cellWidth = this._tagsCell().nativeElement.getBoundingClientRect().width;
      let totalWidth = 0;
      for (const node of childNodes) {
        // 8 is right margin of a chip
        totalWidth += node.getBoundingClientRect().width + 8;

        if (totalWidth > cellWidth) {
          this._tagsCell().nativeElement.classList.add('hover-effects');
          return;
        } else {
          this._tagsCell().nativeElement.classList.remove('hover-effects');
        }
      }
    }
  }

  addTag(tag: Models.Tag): void {
    // [Scenario]: Clicked a tag that's already on the loan, lead, etc.
    if (
      Array.from(this.tags)
        .map((t) => t.guid)
        .includes(tag.guid)
    ) {
      return;
    }

    this.tags.add(tag);
    this.tags = this._sortTagsSet(this.tags);
    this.selectedTags$.next(this.tags);

    // In create mode, entityGuid does not exist
    // If entityGuid is null, save the tags after saving the lead
    if (this.postOnAddingTag()) {
      this._saveTag$(tag)
        .pipe(
          tap(() => {
            this._updateRowData();
          }),
        )
        .subscribe();
    } else {
      // emit the tag
      this.tagAdded.emit(Array.from(this.tags).map((t) => t.guid));
    }
  }

  removeTag(tag: Models.Tag): void {
    if (!this.isEditable() || this.isTagReadOnly(tag)) {
      if (this.tagLocation === 'sidebar' && tag.pipelineType === Models.PipelineType.ServicingLoan) {
        this._emitClickedTag(tag);
      }
      return;
    }

    this._cdr.detectChanges();
    this.tags.delete(tag);
    this.selectedTags$.next(this.tags);
    this.tagRemoved.emit(Array.from(this.tags).map((t) => t.guid));
    if (this.deleteOnRemovingTag()) {
      this._deleteTag$(tag)
        .pipe(
          tap(() => {
            this._updateRowData();
          }),
        )
        .subscribe();
    }
  }

  isTagReadOnly(tag: Models.Tag) {
    return this._readonlyTags.includes(tag.name);
  }

  private _saveTag$(tag: Models.Tag) {
    switch (tag.pipelineType) {
      case Models.PipelineType.Loan:
        return this._api.saveTagToLoan(this.fileDataId(), tag.guid);

      case Models.PipelineType.Lead:
        return this._api.saveTagToLead(this.entityGuid(), tag.guid);

      case Models.PipelineType.LoanApplication:
        return this._api.saveTagToLoanApplication$(tag.guid, this.entityGuid());
    }
  }

  private _deleteTag$(tag: Models.Tag) {
    switch (tag.pipelineType) {
      case Models.PipelineType.Loan:
        return this._api.deleteTagFromLoan(this.fileDataId(), tag.guid);

      case Models.PipelineType.Lead:
        return this._api.deleteTagFromLead(this.entityGuid(), tag.guid);

      case Models.PipelineType.LoanApplication:
        return this._api.deleteTagFromLoanApplication(this.entityGuid(), tag.guid);
    }
  }

  private _updateRowData(): void {
    const rowNode = this.rowNode();
    if (rowNode === undefined) {
      return;
    }

    rowNode?.setDataValue(
      'tags',
      Array.from(this.tags).map((tag) => tag.guid),
    );
  }

  private _assembleTags(allTags: Models.Tag[], pipelineType: Models.PipelineType): Models.Tag[] {
    // All Loans Pipeline shares tags with Loan
    if (pipelineType === Models.PipelineType.AllLoans) {
      pipelineType = Models.PipelineType.Loan;
    }
    const filtered = allTags?.filter((tag) => tag.pipelineType === pipelineType && tag.isGlobal);
    return this._sortTags(filtered);
  }

  /** Sorts tags using custom tagSortOrder (by name) if provided, otherwise alphabetically. */
  private _sortTags(tags: Models.Tag[]): Models.Tag[] {
    if (!tags) {
      return tags;
    }
    if (this.tagSortOrder()) {
      return this._sortTagsByOrder(tags, this.tagSortOrder());
    }
    return this._sortTagsByName(tags);
  }

  private _sortTagsByName(tags: Models.Tag[]): Models.Tag[] {
    return tags?.sort((a, b) => a?.name.localeCompare(b.name));
  }

  /** Sorts tags by position in the provided name order array. Tags not in the array sort to the end alphabetically. */
  private _sortTagsByOrder(tags: Models.Tag[], order: string[]): Models.Tag[] {
    return [...tags].sort((a, b) => {
      const indexA = order.indexOf(a?.name);
      const indexB = order.indexOf(b?.name);
      // Tags not in the order array sort to the end, alphabetically among themselves
      const posA = indexA === -1 ? order.length : indexA;
      const posB = indexB === -1 ? order.length : indexB;
      if (posA !== posB) {
        return posA - posB;
      }
      // Both not in order array — fall back to alphabetical
      return a?.name.localeCompare(b.name);
    });
  }

  private _sortTagsSet(tagsSet: Set<Models.Tag>): Set<Models.Tag> {
    const sortedTags = this._sortTags(Array.from(tagsSet));

    if (this._involvesPriortyTag() && sortedTags?.length > 1) {
      this._sortPriorityTag(sortedTags);
    }

    return new Set(sortedTags);
  }

  // ====== START: Servicing Loans ====== //
  /** Intended for updating selected opportunity in the panel - "Opportunity Analysis". */
  private _emitClickedTag(clickedTag: Models.Tag) {
    this.tagClicked.emit(clickedTag);
  }

  /** Maps Pipeline view to the matching priority tag name. */
  private _getPriorityTagNameForView(activeLayoutName: string) {
    return possibleOpportunities?.find((opp) => opp.viewName === activeLayoutName)?.tagName;
  }

  /** Determines whether priority tags are relevant to current Pipeline. */
  private _involvesPriortyTag() {
    return (
      this._ui.store.value.activePipelineType === Models.PipelineType.ServicingLoan ||
      this._ui.store.value.activePipelineType === Models.PipelineType.AllLoans ||
      this.tagLocation === 'dashboard' ||
      this.tagLocation === 'sidebar'
    );
  }

  /** Moves `priorityTag` in `RouteUiService` to index 0. */
  private _sortPriorityTag(tags: Models.Tag[]) {
    const priorityTag = this._ui.store.value.priorityTag;

    if (!priorityTag) {
      return;
    }

    const priorityTagIndex = tags.findIndex((tag) => tag.name === priorityTag.name);

    if (priorityTagIndex > -1) {
      tags.splice(priorityTagIndex, 1);
      tags.splice(0, 0, priorityTag);
    }
  }

  /** Creates Observable to update `priorityTag` in `RouteUiService` upon changing Pipeline View.  */
  private _updatePriorityTagOnViewChange() {
    //dashboard Opt-Out tag priority
    if (this.tagLocation === 'dashboard' || this.tagLocation === 'sidebar') {
      this.globalTags$
        .pipe(
          takeUntil(this._destroyed$),
          filter((tags) => !!tags?.length),
          take(1),
        )
        .subscribe((tags) => {
          this._ui.stateChange({ priorityTag: tags.find((t) => t?.name === 'Opt-Out') ?? null });
          this.tags = this._sortTagsSet(this.tags);
        });
      return;
    }
    this._ui.activeLayoutID$
      .pipe(
        takeUntil(this._destroyed$),
        withLatestFrom(this._api.layouts.all$, this.globalTags$),
        tap(([activeLayoutID, layouts, tags]) => {
          const activeType = this._ui.store.value.activePipelineType;
          if (this._involvesPriortyTag()) {
            // AllLoans → set Opt-Out as priority
            if (activeType === Models.PipelineType.AllLoans) {
              this._ui.stateChange({ priorityTag: tags.find((t) => t?.name === 'Opt-Out') ?? null });
              this.tags = this._sortTagsSet(this.tags); // re-order chips with priority first
              return;
            }

            // ServicingLoan (existing behavior)
            const activeLayout = layouts.find((l) => l.guid === activeLayoutID);
            this._ui.stateChange({
              priorityTag:
                activeLayout?.visibility === Models.LayoutVisibility.Global
                  ? tags.find((tag) => tag.name === this._getPriorityTagNameForView(activeLayout.displayName))
                  : null,
            });
            this.tags = this._sortTagsSet(this.tags);
            return;
          }

          // Other pipelines
          if (this._ui.store.value.priorityTag) {
            this._ui.stateChange({ priorityTag: null });
            this.tags = this._sortTagsSet(this.tags);
          }
        }),
      )
      .subscribe();
  }

  // ====== END: Servicing Loans ====== //

  closeAllOtherPanels(keepOpen: Popover) {
    this._ui.closeDropdowns$.next(keepOpen);
    this.isDropdownOpen = false;
    this._tagsChipsService.setDropdown(this.isDropdownOpen ? this.tagsCellId : null);
  }
}
